import { action, computed, makeObservable, observable, runInAction } from 'mobx'

import {
  IGeoJson2DGeographicCoordinates,
  IGlobeView,
  IGlobeViewSpecificItemData,
  IGlobeViewStyleInput,
  LocationType,
} from '~/client/graph'
import Guard from '~/client/src/shared/utils/Guard'
import { liveCoordinates } from '~/client/src/shared/utils/liveEquipmentPosition'
import {
  IAncestorCoordinates,
  ILocationDisplayContext,
  resolveLocationCoordinates,
  resolveLocationDisplayContext,
} from '~/client/src/shared/utils/resolveLocationDisplayContext'

import GlobeView from '../../models/GlobeView'
import LocationBase from '../../models/LocationObjects/LocationBase'
import * as e from '../EventStore/eventConstants'
import EventsStore from '../EventStore/Events.store'
import LocationAttributesStore from './LocationAttributes.store'
import SitemapItemsStore from './SitemapItems.store'

export default class GlobeViewsStore {
  @observable public isDataReceived = false

  public constructor(
    private readonly eventsStore: EventsStore,
    private readonly locationAttributesStore: LocationAttributesStore,
    private readonly sitemapItemsStore?: SitemapItemsStore,
  ) {
    makeObservable(this)
    Guard.requireAll({
      eventsStore,
    })
  }

  @computed
  public get list(): GlobeView[] {
    return Array.from(this.byId.values())
  }

  public get appState() {
    return this.eventsStore.appState
  }

  public get byId() {
    const { globeViews } = this.eventsStore.appState
    return globeViews
  }

  public globeAssignedLocations(globe: GlobeView): LocationBase[] {
    return globe
      ? this.locationAttributesStore.globeAssignedLocations(globe.id)
      : []
  }

  /**
   * Priority:
   *   1. Exact tag — globe tagged to the workflow location itself.
   *   2. Closest non-project ancestor tag — walk up the parent chain,
   *      stopping before ProjectLocation.
   *   3. Displayed-on-map — first candidate globe that renders the
   *      workflow location as a visible sitemap item.
   *   4. Project-tagged fallback — first candidate globe tagged to the
   *      ProjectLocation.
   */
  public findBestGlobeForLocation(
    locationId: string,
    candidateGlobeIds: string[],
  ): string | null {
    return this.resolveLocationDisplayContext(locationId, candidateGlobeIds)
      .globeViewId
  }

  /**
   * Finds the nearest coordinates in this Location's ancestor chain,
   * ignoring globe visibility. Coordinates live on a shared SitemapItem,
   * so this returns a coord whenever any ancestor has one set anywhere
   * in the system. Used as the seed for dashed pending-placement pins
   * and any future "snap to parent" flow.
   */
  public resolveLocationCoordinates(
    locationId: string,
    options: { includeProject?: boolean } = {},
  ): IAncestorCoordinates | null {
    if (!this.sitemapItemsStore) {
      return null
    }
    const { hierarchyAllAttributesMap } = this.locationAttributesStore
    return resolveLocationCoordinates({
      locationId,
      getLocation: id => hierarchyAllAttributesMap[id] ?? null,
      getSitemapItemByLocationId: id => ({
        coordinates: this.drawnCoordinates(id, hierarchyAllAttributesMap[id]),
      }),
      includeProject: options.includeProject,
    })
  }

  public effectiveLocationCoordinates(
    locationId: string,
  ): IGeoJson2DGeographicCoordinates | null {
    return this.drawnCoordinates(
      locationId,
      this.locationAttributesStore.hierarchyAllAttributesMap[locationId],
    )
  }

  private drawnCoordinates(
    locationId: string,
    location: LocationBase,
  ): IGeoJson2DGeographicCoordinates | null {
    const placed =
      this.sitemapItemsStore?.byAssignedId.get(locationId)?.coordinates
    if (!placed) {
      return null
    }
    return liveCoordinates(location) ?? placed
  }

  public resolveLocationDisplayContext(
    locationId: string,
    candidateGlobeIds: string[],
  ): ILocationDisplayContext {
    return resolveLocationDisplayContext({
      locationId,
      candidateGlobeIds,
      projectLocationAssignedGlobes: this.projectLocationAssignedGlobes,
      getLocation: id =>
        this.locationAttributesStore.hierarchyAllAttributesMap[id] ?? null,
      getGlobe: id => this.byId.get(id) ?? null,
      getSitemapItem: id => this.sitemapItemsStore?.byId.get(id) ?? null,
    })
  }

  @computed
  private get projectLocationAssignedGlobes(): string[] {
    const seen = new Set<string>()
    for (const loc of Object.values(
      this.locationAttributesStore.hierarchyAllAttributesMap,
    )) {
      if (loc?.is(LocationType.ProjectLocation)) {
        loc.assignedGlobes?.forEach(g => seen.add(g))
      }
    }
    return Array.from(seen)
  }

  @action.bound
  public clearList() {
    this.isDataReceived = false
    this.byId.clear()
  }

  @action.bound
  public receiveList(list: IGlobeView[]) {
    this.clearList()

    list.forEach(dto => {
      const globeView = GlobeView.fromDto(dto)
      this.byId.set(globeView.id, globeView)
    })

    this.isDataReceived = true
  }

  @action.bound
  public receiveOne(id: string, dto: IGlobeView) {
    if (dto) {
      const existingGlobeView = this.byId.get(dto.id)
      dto.items = existingGlobeView?.items
      this.byId.set(dto.id, GlobeView.fromDto(dto))
    } else {
      this.byId.delete(id)
    }
  }

  @action.bound
  public receiveOneItem(id: string, dto: IGlobeViewSpecificItemData) {
    if (dto) {
      const globeView = this.byId.get(dto.globeViewId)
      if (!globeView) {
        return
      }
      globeView.setItemDisplayData(dto.sitemapItemId, dto)
      this.byId.set(dto.globeViewId, globeView)
    } else {
      const globeView = this.getGlobeViewByItemId(id)
      if (globeView) {
        globeView.removeItemDisplayData(id)
        this.byId.set(globeView.id, globeView)
      }
    }
  }

  private getGlobeViewByItemId(id: string) {
    const globeViews = Array.from(this.byId, ([, value]) => value)
    return globeViews.find(g => g.items.some(i => i.id === id))
  }

  @action.bound
  public deleteGlobeViewItem(
    itemId: string,
    callbackFn?: (id: string) => void,
    shouldApplyChangesBeforeSave?: boolean,
    avoidSaving?: boolean,
  ) {
    if (!avoidSaving) {
      this.eventsStore.dispatch(
        e.DELETE_GLOBE_VIEW_ITEM_DATA,
        itemId,
        callbackFn,
      )
    }
  }

  @action.bound
  public saveGlobeViewItems(
    items: IGlobeViewSpecificItemData[],
    globeViewId: string,
    callbackFn?: (id: string) => void,
    shouldApplyChangesBeforeSave?: boolean,
    avoidSaving?: boolean,
    errorCallbackFn?: (error: unknown) => void,
  ) {
    const { getPolylineItemDtos, getRectangleItemDtos, getCircleItemDtos } =
      GlobeView

    // Snapshot the pre-optimistic display data so we can roll back if the
    // save fails. Without this, a failed retry would see the item as
    // already-displayed locally, changeGlobeItemDataVisibility would
    // filter it out of the save payload, and the queue would advance
    // as if the backend had accepted the placement.
    let rollbackOptimisticUpdate: (() => void) | null = null

    if (shouldApplyChangesBeforeSave) {
      const globe = this.byId.get(globeViewId)
      if (globe) {
        const snapshots = items.map(i => ({
          sitemapItemId: i.sitemapItemId,
          prev: globe.hasDisplayData(i.sitemapItemId)
            ? { ...globe.getItemDisplayData(i.sitemapItemId) }
            : null,
        }))
        items.forEach(i => globe.setItemDisplayData(i.sitemapItemId, i))
        this.byId.set(globe.id, globe.getFullCopy())

        rollbackOptimisticUpdate = () => {
          const currentGlobe = this.byId.get(globeViewId)
          if (!currentGlobe) {
            return
          }
          runInAction(() => {
            snapshots.forEach(s => {
              if (s.prev) {
                currentGlobe.setItemDisplayData(s.sitemapItemId, s.prev)
                return
              }
              const idx = currentGlobe.items?.findIndex(
                x => x.sitemapItemId === s.sitemapItemId,
              )
              if (idx != null && idx !== -1) {
                currentGlobe.items.splice(idx, 1)
              }
            })
            this.byId.set(currentGlobe.id, currentGlobe.getFullCopy())
          })
        }
      }
    }

    if (!avoidSaving) {
      this.eventsStore.dispatch(
        e.SAVE_GLOBE_VIEW_ITEM_DATA,
        getPolylineItemDtos(items),
        getRectangleItemDtos(items),
        getCircleItemDtos(items),
        callbackFn,
        (error: unknown) => {
          rollbackOptimisticUpdate?.()
          errorCallbackFn?.(error)
        },
      )
    }
  }

  @action.bound
  public async saveGlobeViewStyle(
    style: IGlobeViewStyleInput,
    globeViewId: string,
    callbackFn?: (id: string) => void,
  ) {
    this.eventsStore.dispatch(
      e.SAVE_GLOBE_VIEW_STYLE,
      style,
      globeViewId,
      callbackFn,
    )
  }

  @action.bound
  public save(
    globeView: GlobeView,
    callbackFn?: (id: string) => void,
    shouldApplyChangesBeforeSave?: boolean,
    avoidSaving?: boolean,
  ) {
    const { id: projectId } = this.eventsStore.appState.activeProject

    const globeViewInput: IGlobeView = {
      id: globeView.id,
      name: globeView.name,
      projectId: globeView.projectId || projectId,
      bounds: globeView.bounds,
      zoom: globeView.zoom,
      pitch: globeView.pitch,
      center: globeView.center,
      bearing: globeView.bearing,
      geoCorners: globeView.geoCorners,
      altitude: globeView.altitude,
      mapFiles: globeView.mapFiles,
      isOrientationLocked: globeView.isOrientationLocked,
      filledImage: globeView.filledImage,
      style: globeView.style,
    }

    if (shouldApplyChangesBeforeSave && globeView.id) {
      this.byId.set(globeView.id, globeView.getFullCopy())
    }
    // A brand-new globe has no id yet, so the merge above cannot run for it and the store only
    // learns of it when the server echo lands — after the save's own callback. adoptSaved closes
    // that window for the caller that holds the instance.

    if (!avoidSaving) {
      this.eventsStore.dispatch(e.SAVE_GLOBE_VIEW, globeViewInput, callbackFn)
    }
  }

  /** Puts a just-saved globe into the store under the id the save returned, so everything reading
   *  byId — selectedGlobeView included — resolves it without waiting for the server echo. */
  @action.bound
  public adoptSaved(globeView: GlobeView, id: string): GlobeView {
    // An echoed copy carries the server's fields; don't replace it with the local one.
    const echoed = this.byId.get(id)
    if (echoed) {
      return echoed
    }
    globeView.setId(id)
    const stored = globeView.getFullCopy()
    this.byId.set(id, stored)
    return stored
  }

  @action.bound
  public removeOne(globeViewId: string, callbackFn?: () => void) {
    if (!this.byId.has(globeViewId)) {
      return
    }

    this.byId.delete(globeViewId)

    this.eventsStore.dispatch(e.DELETE_GLOBE_VIEW, globeViewId, callbackFn)
  }
}
